import {
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Select,
  Textarea,
} from '@chakra-ui/react';
import { ChevronDown, X } from 'lucide-react';

import {
  PromptFields,
  VariableKey,
  variableKind,
  type ContextSlots,
} from '@/components/ui/prompt-library-clean';
import {
  useCreatePromptTemplate,
  usePromptContext,
  usePromptGroups,
  useUpdatePromptTemplate,
} from '@/store/prompts';
import { PROMPT_AGENT_IDS, PromptTemplateRecord } from '@/types/prompts';
import { contextNamesByGroup, contextNamesByName } from '@/utils/promptContext';

import { PromptAgentAvatar } from './PromptAgentAvatar';
import {
  BODY_MAX,
  contextNamesLine,
  detectVariables,
  draftFromTemplate,
  draftProblems,
  isDraftValid,
  isForbidden,
  makeVariable,
  NOT_CONTEXT_NOTE,
  PromptDraft,
  sameDraft,
  serverError,
  ServerErrorField,
  TEAM_FORBIDDEN,
  TITLE_MAX,
  toCreateBody,
  toDraft,
  toUpdateBody,
  unknownContextNames,
  variableNameProblem,
} from './promptDraft';

export type PromptEditorSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Edit this template: Save writes a new Template Version. */
  template?: PromptTemplateRecord;
  /** Prefill for a new prompt, e.g. Save to prompts from a sent message. */
  initial?: Partial<PromptDraft>;
  /** The sheet's heading; defaults to "New prompt" or "Edit prompt". */
  heading?: string;
  /**
   * A team prompt (#595): created with `scope: "team"`, its group one of the
   * VH3 or team groups (never My prompts). Defaults to the template's own
   * scope, else personal.
   */
  scope?: 'personal' | 'team';
  onSaved?: (template: PromptTemplateRecord) => void;
};

const AGENTS = [
  { id: PROMPT_AGENT_IDS.connie, label: 'Connie' },
  { id: PROMPT_AGENT_IDS.tia, label: 'Tia' },
  { id: PROMPT_AGENT_IDS.salma, label: 'Salma' },
];

const BODY_MIN_HEIGHT = 120;
const BODY_MAX_HEIGHT = 420;
const count = new Intl.NumberFormat('en');

/**
 * The prompt editor sheet (VH3_CONNECT #594): one right-side drawer for New,
 * Edit and Save to prompts. Title, group, agents, body and the variables read
 * from it, with the server's limits mirrored here so Save only goes when the
 * server would take it.
 */
export function PromptEditorSheet({
  isOpen,
  onClose,
  template,
  initial,
  heading,
  scope,
  onSaved,
}: PromptEditorSheetProps) {
  const titleRef = useRef<HTMLInputElement>(null);
  return (
    <Drawer
      isOpen={isOpen}
      placement='right'
      onClose={onClose}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      initialFocusRef={titleRef}
      size='md'
    >
      <DrawerOverlay />
      {/* The form mounts with the drawer, so each open starts fresh. */}
      <EditorForm
        titleRef={titleRef}
        template={template}
        initial={initial}
        heading={heading}
        scope={scope}
        onClose={onClose}
        onSaved={onSaved}
      />
    </Drawer>
  );
}

type EditorFormProps = Omit<PromptEditorSheetProps, 'isOpen'> & {
  titleRef: React.RefObject<HTMLInputElement | null>;
};

function Counter({
  value,
  max,
  cy,
}: {
  value: number;
  max: number;
  cy: string;
}) {
  return (
    <span
      className='prompt-editor__count'
      data-over={value > max ? '' : undefined}
      data-cy={cy}
    >
      {count.format(value)}/{count.format(max)}
    </span>
  );
}

function FieldError({ cy, children }: { cy: string; children: ReactNode }) {
  return (
    <p className='prompt-editor__error' role='alert' data-cy={cy}>
      {children}
    </p>
  );
}

function EditorForm({
  titleRef,
  template,
  initial,
  heading,
  scope: scopeProp,
  onClose,
  onSaved,
}: EditorFormProps) {
  const isTeam =
    (scopeProp ?? (template?.scope === 'team' ? 'team' : 'personal')) ===
    'team';
  const uid = useId();
  const [start] = useState(() =>
    template ? draftFromTemplate(template) : toDraft(initial)
  );
  const [draft, setDraft] = useState(start);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [error, setError] = useState<{
    field: ServerErrorField;
    message: string;
  } | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const groupsQuery = usePromptGroups();
  const createMutation = useCreatePromptTemplate();
  const updateMutation = useUpdatePromptTemplate();
  const isSaving = createMutation.isPending || updateMutation.isPending;

  // VH3's list of Context Variables (spec FSI #1230). Empty with the switch
  // off or while loading: no menu, and Xano makes the save check then.
  const contextNames = usePromptContext(null).context.names;
  const contextByName = useMemo(
    () => contextNamesByName(contextNames),
    [contextNames]
  );
  // The preview's context fields, by label ("Job type", not "job.type").
  const contextSlots = useMemo<ContextSlots>(
    () =>
      Object.fromEntries(
        contextNames.map(item => [item.name, { label: item.label }])
      ),
    [contextNames]
  );
  const variables = useMemo(
    () => detectVariables(draft.body, contextNames),
    [draft.body, contextNames]
  );
  const unknownNames = useMemo(
    () => unknownContextNames(draft.body, contextNames),
    [draft.body, contextNames]
  );
  const problems = draftProblems(draft, contextNames);
  const isDirty = !sameDraft(draft, start, contextNames);
  const canSave = isDraftValid(draft, contextNames) && !isSaving;

  const update = (patch: Partial<PromptDraft>) => {
    setDraft(current => ({ ...current, ...patch }));
    setConfirmDiscard(false);
    if (error) setError(null);
  };

  const setVariable = (
    name: string,
    key: 'description' | 'default',
    value: string
  ) =>
    update({
      variables: {
        ...draft.variables,
        [name]: {
          description: draft.variables[name]?.description ?? '',
          default: draft.variables[name]?.default ?? '',
          [key]: value,
        },
      },
    });

  const toggleAgent = (agentId: number) => {
    const on = draft.agentIds.includes(agentId);
    // At least one agent: the last one stays on.
    if (on && draft.agentIds.length === 1) return;
    update({
      agentIds: on
        ? draft.agentIds.filter(id => id !== agentId)
        : [...draft.agentIds, agentId],
    });
  };

  /* ----- The body grows with its text ----- */
  useLayoutEffect(() => {
    const area = bodyRef.current;
    if (!area) return;
    area.style.height = 'auto';
    area.style.height = `${Math.min(
      BODY_MAX_HEIGHT,
      Math.max(BODY_MIN_HEIGHT, area.scrollHeight + 2)
    )}px`;
  }, [draft.body]);

  /* ----- Insert context: a listed name goes in at the cursor ----- */
  // The cursor as the body last had it: the menu takes focus from the body,
  // and a body never focused has none, so the name goes on the end.
  const caretRef = useRef<{ start: number; end: number } | null>(null);
  // Set by an insert: the layout effect places the cursor, and the menu's
  // focus return to its button is sent on to the body.
  const insertedRef = useRef(false);
  const returnToBodyRef = useRef(false);
  const rememberCaret = () => {
    const area = bodyRef.current;
    if (area) {
      caretRef.current = { start: area.selectionStart, end: area.selectionEnd };
    }
  };

  const insertContext = (name: string) => {
    const length = draft.body.length;
    const at = caretRef.current ?? { start: length, end: length };
    const from = Math.min(at.start, length);
    const to = Math.min(Math.max(at.end, from), length);
    const token = `{${name}}`;
    insertedRef.current = true;
    returnToBodyRef.current = true;
    window.setTimeout(() => {
      returnToBodyRef.current = false;
    }, 1000);
    caretRef.current = { start: from + token.length, end: from + token.length };
    update({
      body: `${draft.body.slice(0, from)}${token}${draft.body.slice(to)}`,
    });
    setSelectHint(false);
  };

  const restoreCaret = () => {
    const area = bodyRef.current;
    const at = caretRef.current;
    if (!area || !at) return;
    area.focus();
    area.setSelectionRange(at.start, at.end);
  };

  // After the insert the cursor sits behind the name, in the body.
  useLayoutEffect(() => {
    if (!insertedRef.current) return;
    insertedRef.current = false;
    restoreCaret();
  }, [draft.body]);

  /* ----- Make variable: the selected words become {name} ----- */
  const [naming, setNaming] = useState<{
    start: number;
    end: number;
    name: string;
    problem: string | null;
  } | null>(null);
  const [selectHint, setSelectHint] = useState(false);

  const startNaming = () => {
    const area = bodyRef.current;
    const from = area?.selectionStart ?? 0;
    const to = area?.selectionEnd ?? 0;
    if (!area || from === to || !draft.body.slice(from, to).trim()) {
      setSelectHint(true);
      setNaming(null);
      return;
    }
    setSelectHint(false);
    setNaming({ start: from, end: to, name: '', problem: null });
  };

  const finishNaming = () => {
    if (!naming) return;
    const name = naming.name.trim();
    const problem = variableNameProblem(name, contextNames);
    if (problem) {
      setNaming({ ...naming, problem });
      return;
    }
    const words = draft.body.slice(naming.start, naming.end).trim();
    update({
      body: makeVariable(draft.body, naming.start, naming.end, name),
      // A Context Variable has no description or default to keep.
      ...(!contextByName.has(name) && {
        variables: {
          ...draft.variables,
          [name]: draft.variables[name] ?? { description: '', default: words },
        },
      }),
    });
    setNaming(null);
  };

  /* ----- Closing: ask before throwing away changes ----- */
  const requestClose = () => {
    if (isSaving) return;
    if (isDirty) setConfirmDiscard(true);
    else onClose();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    if (confirmDiscard) setConfirmDiscard(false);
    else requestClose();
  };

  const save = async () => {
    if (!canSave) return;
    setError(null);
    // A new team prompt always sits in a group: the first one until another is
    // picked. An existing template keeps its group unless one is picked.
    const saving = { ...draft, groupId: groupId };
    try {
      let saved: PromptTemplateRecord;
      if (template) {
        const body = toUpdateBody(saving, start, contextNames);
        if (Object.keys(body).length === 0) {
          onClose();
          return;
        }
        saved = await updateMutation.mutateAsync({
          templateId: template.id,
          body,
        });
      } else {
        saved = await createMutation.mutateAsync(
          toCreateBody(saving, isTeam ? 'team' : 'personal', contextNames)
        );
      }
      onSaved?.(saved);
      onClose();
    } catch (caught) {
      setError(
        isTeam && isForbidden(caught)
          ? { field: 'form', message: TEAM_FORBIDDEN }
          : serverError(caught)
      );
    }
  };

  // A team prompt picks from the VH3 and team groups only. An existing
  // template's own group is always offered, even when the tenant hid it.
  const groups = (groupsQuery.data ?? []).filter(
    group =>
      (template && group.id === start.groupId) ||
      (!group.hidden && (!isTeam || group.scope !== 'personal'))
  );
  const groupId =
    isTeam && !template && !groups.some(group => group.id === draft.groupId)
      ? (groups[0]?.id ?? draft.groupId)
      : draft.groupId;
  const kind = isTeam ? 'team prompt' : 'prompt';
  const title = heading ?? (template ? `Edit ${kind}` : `New ${kind}`);
  const errorFor = (field: ServerErrorField) =>
    error?.field === field ? error.message : null;

  return (
    <DrawerContent
      className='prompt-editor'
      data-cy='prompt-editor'
      w='100%'
      maxW={{ base: '100vw', sm: '480px' }}
      onKeyDown={onKeyDown}
      aria-labelledby={`${uid}-heading`}
    >
      <DrawerHeader className='prompt-editor__head'>
        <h2 id={`${uid}-heading`} className='prompt-editor__heading'>
          {title}
        </h2>
        <IconButton
          aria-label='Close'
          variant='ghost'
          size='sm'
          icon={<X size={16} strokeWidth={1.5} />}
          onClick={requestClose}
        />
      </DrawerHeader>

      <DrawerBody className='prompt-editor__body'>
        <div className='prompt-editor__scroll'>
          {/* Title */}
          <div className='prompt-editor__field'>
            <div className='prompt-editor__label-row'>
              <label className='prompt-editor__label' htmlFor={`${uid}-title`}>
                Title
              </label>
              <Counter
                value={draft.title.length}
                max={TITLE_MAX}
                cy='prompt-editor-title-count'
              />
            </div>
            <Input
              ref={titleRef}
              id={`${uid}-title`}
              size='sm'
              value={draft.title}
              placeholder='What it does, in a few words'
              autoComplete='off'
              isInvalid={draft.title.length > TITLE_MAX}
              onChange={event => update({ title: event.target.value })}
              data-cy='prompt-editor-title'
            />
            {errorFor('title') && (
              <FieldError cy='prompt-editor-title-error'>
                {errorFor('title')}
              </FieldError>
            )}
          </div>

          {/* Group */}
          <div className='prompt-editor__field'>
            <label className='prompt-editor__label' htmlFor={`${uid}-group`}>
              Group
            </label>
            <Select
              id={`${uid}-group`}
              size='sm'
              value={String(groupId)}
              onChange={event =>
                update({ groupId: Number(event.target.value) })
              }
              data-cy='prompt-editor-group'
            >
              {!isTeam && <option value='0'>My prompts</option>}
              {groups.map(group => (
                <option key={group.id} value={String(group.id)}>
                  {group.label}
                </option>
              ))}
            </Select>
          </div>

          {/* Sends to */}
          <div className='prompt-editor__field'>
            <span className='prompt-editor__label' id={`${uid}-agents`}>
              Sends to
            </span>
            <div
              className='prompt-editor__chips'
              role='group'
              aria-labelledby={`${uid}-agents`}
            >
              {AGENTS.map(agent => {
                const on = draft.agentIds.includes(agent.id);
                return (
                  <button
                    key={agent.id}
                    type='button'
                    className='prompt-editor__chip'
                    aria-pressed={on}
                    title={
                      on && draft.agentIds.length === 1
                        ? 'A prompt sends to at least one agent'
                        : undefined
                    }
                    onClick={() => toggleAgent(agent.id)}
                    data-cy='prompt-editor-agent'
                  >
                    <PromptAgentAvatar agentId={agent.id} />
                    {agent.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Body */}
          <div className='prompt-editor__field'>
            <div className='prompt-editor__label-row'>
              <label className='prompt-editor__label' htmlFor={`${uid}-body`}>
                Prompt
              </label>
              <Counter
                value={draft.body.length}
                max={BODY_MAX}
                cy='prompt-editor-body-count'
              />
            </div>
            <Textarea
              ref={bodyRef}
              id={`${uid}-body`}
              size='sm'
              value={draft.body}
              placeholder='The ask, with {variables} for the parts that change'
              resize='none'
              isInvalid={draft.body.length > BODY_MAX}
              onChange={event => {
                update({ body: event.target.value });
                setSelectHint(false);
                returnToBodyRef.current = false;
                rememberCaret();
              }}
              onSelect={rememberCaret}
              onBlur={rememberCaret}
              data-cy='prompt-editor-body'
            />
            {errorFor('body') && (
              <FieldError cy='prompt-editor-body-error'>
                {errorFor('body')}
              </FieldError>
            )}

            <div className='prompt-editor__make'>
              {naming ? (
                <div className='prompt-editor__naming'>
                  <Input
                    size='xs'
                    autoFocus
                    value={naming.name}
                    placeholder='Variable name'
                    aria-label='Variable name'
                    autoComplete='off'
                    spellCheck={false}
                    onChange={event =>
                      setNaming({
                        ...naming,
                        name: event.target.value,
                        problem: null,
                      })
                    }
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        finishNaming();
                      } else if (event.key === 'Escape') {
                        event.preventDefault();
                        event.stopPropagation();
                        setNaming(null);
                      }
                    }}
                    data-cy='prompt-editor-variable-name'
                  />
                  <button
                    type='button'
                    className='prompt-btn prompt-btn--ghost'
                    onClick={finishNaming}
                  >
                    Make
                  </button>
                  <button
                    type='button'
                    className='prompt-btn prompt-btn--ghost'
                    onClick={() => setNaming(null)}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type='button'
                  className='prompt-btn prompt-btn--ghost'
                  // Keep the body's selection: the button takes no focus on press.
                  onMouseDown={event => event.preventDefault()}
                  onClick={startNaming}
                  data-cy='prompt-editor-make-variable'
                >
                  Make variable
                </button>
              )}
              {!naming && contextNames.length > 0 && (
                <Menu isLazy strategy='fixed' placement='bottom-start'>
                  <MenuButton
                    type='button'
                    className='prompt-btn prompt-btn--ghost prompt-editor__insert'
                    // Chakra hands focus back to this button when the menu
                    // closes; after an insert it goes on to the body.
                    onFocus={() => {
                      if (!returnToBodyRef.current) return;
                      returnToBodyRef.current = false;
                      restoreCaret();
                    }}
                    data-cy='prompt-editor-insert-context'
                  >
                    Insert context
                    <ChevronDown size={14} strokeWidth={1.5} aria-hidden />
                  </MenuButton>
                  <MenuList
                    className='prompt-editor__context-list'
                    data-cy='prompt-editor-context-list'
                    // Esc closes the menu, not the sheet.
                    onKeyDown={event => {
                      if (event.key === 'Escape') event.stopPropagation();
                    }}
                  >
                    {contextNamesByGroup(contextNames).map(section => (
                      <MenuGroup key={section.group} title={section.group}>
                        {section.items.map(item => (
                          <MenuItem
                            key={item.name}
                            className='prompt-editor__context-item'
                            onClick={() => insertContext(item.name)}
                            data-cy='prompt-editor-context-item'
                            data-name={item.name}
                          >
                            <span className='prompt-editor__context-head'>
                              <span className='prompt-editor__context-label'>
                                {item.label}
                              </span>
                              <span className='prompt-editor__context-fills'>
                                {item.fills_in}
                              </span>
                            </span>
                            <span className='prompt-editor__context-hint'>
                              {item.hint}
                            </span>
                          </MenuItem>
                        ))}
                      </MenuGroup>
                    ))}
                  </MenuList>
                </Menu>
              )}
              {naming?.problem && (
                <span className='prompt-editor__note prompt-editor__note--warn'>
                  {naming.problem}
                </span>
              )}
              {!naming && selectHint && (
                <span className='prompt-editor__note'>
                  Select words in the prompt first
                </span>
              )}
            </div>
          </div>

          {/* Preview: the same slots the fill step draws, read-only */}
          {draft.body.trim() && (
            <div className='prompt-editor__field'>
              <div className='prompt-editor__label-row'>
                <span className='prompt-editor__label'>Preview</span>
                <VariableKey
                  kinds={[
                    ...new Set(
                      variables.map(variable =>
                        variableKind(variable.name, contextSlots)
                      )
                    ),
                  ]}
                />
              </div>
              <div
                className='prompt-editor__preview'
                data-cy='prompt-editor-preview'
                inert
              >
                <PromptFields
                  body={draft.body}
                  values={{}}
                  contexts={contextSlots}
                />
              </div>
            </div>
          )}

          {/* Variables */}
          {variables.length > 0 && (
            <div className='prompt-editor__field'>
              <span className='prompt-editor__label'>Variables</span>
              <div className='prompt-editor__variables'>
                {variables.map(variable => {
                  const known = contextByName.get(variable.name);
                  const unknown = unknownNames.find(
                    item => item.name === variable.name
                  );
                  return (
                    <div
                      key={variable.name}
                      className='prompt-editor__variable'
                      data-cy='prompt-editor-variable'
                      data-context={known ? '' : undefined}
                      data-kind={variableKind(variable.name, contextSlots)}
                    >
                      <span className='prompt-editor__variable-name'>
                        {`{${variable.name}}`}
                      </span>
                      {known ? (
                        // A Context Variable: the chat fills it, so there is
                        // nothing to describe or default.
                        <span
                          className='prompt-editor__note'
                          data-cy='prompt-editor-context-note'
                        >
                          <span className='prompt-editor__context-label'>
                            {known.label}
                          </span>
                          {` · ${known.hint} · ${known.fills_in}`}
                        </span>
                      ) : unknown ? (
                        <span
                          className='prompt-editor__note prompt-editor__note--warn'
                          data-cy='prompt-editor-unknown-context'
                        >
                          {NOT_CONTEXT_NOTE}.
                          {unknown.suggestion && (
                            <>
                              {' Did you mean '}
                              <button
                                type='button'
                                className='prompt-editor__suggestion'
                                onClick={() =>
                                  update({
                                    body: draft.body
                                      .split(`{${unknown.name}}`)
                                      .join(`{${unknown.suggestion}}`),
                                  })
                                }
                                data-cy='prompt-editor-suggestion'
                              >{`{${unknown.suggestion}}`}</button>
                              {'?'}
                            </>
                          )}
                        </span>
                      ) : variable.dotted ? (
                        // The list has not loaded: Xano checks it on save.
                        <span className='prompt-editor__note'>
                          Checked when you save
                        </span>
                      ) : (
                        <div className='prompt-editor__variable-fields'>
                          <Input
                            size='xs'
                            placeholder='Description'
                            aria-label={`${variable.name} description`}
                            value={
                              draft.variables[variable.name]?.description ?? ''
                            }
                            onChange={event =>
                              setVariable(
                                variable.name,
                                'description',
                                event.target.value
                              )
                            }
                          />
                          <Input
                            size='xs'
                            placeholder='Default'
                            aria-label={`${variable.name} default`}
                            value={
                              draft.variables[variable.name]?.default ?? ''
                            }
                            onChange={event =>
                              setVariable(
                                variable.name,
                                'default',
                                event.target.value
                              )
                            }
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {unknownNames.length > 0 && (
                <p
                  className='prompt-editor__note'
                  data-cy='prompt-editor-context-names'
                >
                  Context variables: {contextNamesLine(contextNames)}
                </p>
              )}
              {errorFor('variables') && (
                <FieldError cy='prompt-editor-variables-error'>
                  {errorFor('variables')}
                </FieldError>
              )}
            </div>
          )}
        </div>
      </DrawerBody>

      <DrawerFooter className='prompt-editor__foot'>
        {errorFor('form') && (
          <FieldError cy='prompt-editor-form-error'>
            {errorFor('form')}
          </FieldError>
        )}
        {confirmDiscard ? (
          <div className='prompt-editor__actions' role='group'>
            <span className='prompt-editor__confirm'>Discard changes?</span>
            <button
              type='button'
              className='prompt-btn prompt-btn--ghost'
              autoFocus
              onClick={() => setConfirmDiscard(false)}
              data-cy='prompt-editor-keep'
            >
              Keep editing
            </button>
            <button
              type='button'
              className='prompt-btn prompt-btn--primary'
              onClick={onClose}
              data-cy='prompt-editor-discard'
            >
              Discard
            </button>
          </div>
        ) : (
          <div className='prompt-editor__actions'>
            <button
              type='button'
              className='prompt-btn prompt-btn--ghost'
              onClick={requestClose}
              data-cy='prompt-editor-cancel'
            >
              Cancel
            </button>
            <button
              type='button'
              className='prompt-btn prompt-btn--primary'
              disabled={!canSave}
              aria-describedby={
                problems.variables ? `${uid}-reserved` : undefined
              }
              onClick={() => void save()}
              data-cy='prompt-editor-save'
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
            {problems.variables && (
              <span id={`${uid}-reserved`} className='sr-only'>
                {NOT_CONTEXT_NOTE}
              </span>
            )}
          </div>
        )}
      </DrawerFooter>
    </DrawerContent>
  );
}
